import React, { useState, useEffect } from 'react';
import Styles from './RefineVoice.module.scss';
import { FiCheck, FiX, FiSkipForward, FiRefreshCw, FiThumbsUp, FiThumbsDown, FiEdit3, FiChevronRight, FiAward } from 'react-icons/fi';
import contentTrainingService from 'services/contentTrainingService';
import { success as successToast, error as errorToast } from 'helpers/toaster';

const EXERCISE_TYPES = [
  { id: 'compare', label: 'Compare & Select', description: 'Pick the version that sounds more like you' },
  { id: 'rate', label: 'Rate Generated Content', description: 'Tell us how well AI matched your voice' },
  { id: 'identify', label: 'Spot Your Voice', description: 'Find which content sounds like you' },
];

const FEEDBACK_TAGS = [
  'Tone', 'Word choice', 'Opening', 'Length', 'Structure', 'CTA', 'Too formal', 'Too casual', 'On brand', 'Off brand'
];

const RATING_OPTIONS = [
  { value: 5, emoji: '😍', label: 'Perfect' },
  { value: 4, emoji: '😊', label: 'Good' },
  { value: 3, emoji: '😐', label: 'Okay' },
  { value: 2, emoji: '😕', label: 'Off' },
  { value: 1, emoji: '😖', label: 'Very Off' },
];

const RefineVoice = ({ brandId, samples, onRefresh }) => {
  const [currentExercise, setCurrentExercise] = useState(null);
  const [exerciseType, setExerciseType] = useState('compare');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [customRewrite, setCustomRewrite] = useState('');
  const [showRewrite, setShowRewrite] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [learnedPreferences, setLearnedPreferences] = useState([]);
  const [rating, setRating] = useState(null);

  const hasEnoughSamples = samples.length >= 3;

  // Generate a new exercise
  const generateExercise = async (type = exerciseType) => {
    if (!brandId) return;

    setIsLoading(true);
    setSelectedOption(null);
    setSelectedTags([]);
    setCustomRewrite('');
    setShowRewrite(false);
    setRating(null);

    try {
      const response = await contentTrainingService.generateExercise(brandId, type);
      if (response.data.success) {
        setCurrentExercise(response.data.data);
      }
    } catch (error) {
      console.error('Error generating exercise:', error);
      // Generate mock exercise for demo
      generateMockExercise(type);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate mock exercise for demo purposes
  const generateMockExercise = (type) => {
    const mockExercises = {
      compare: {
        type: 'compare',
        topic: 'Product announcement',
        options: [
          {
            id: 'a',
            content: 'We are pleased to announce the launch of our new analytics dashboard, designed to help teams make better data-driven decisions with comprehensive reporting tools.'
          },
          {
            id: 'b',
            content: 'It\'s here. The analytics dashboard you\'ve been asking for just went live. Real-time data. Beautiful reports. Zero learning curve.'
          }
        ]
      },
      rate: {
        type: 'rate',
        topic: 'Tips for productivity',
        content: 'Struggling with productivity? Here are 5 tools that changed everything for our team. Thread 🧵',
        generated: true
      },
      identify: {
        type: 'identify',
        topic: 'Feature announcement',
        options: [
          { id: 'a', content: 'Excited to share that we\'ve just released...' },
          { id: 'b', content: 'BIG NEWS! We just launched something huge...' },
          { id: 'c', content: 'okay so here\'s the deal - we built this thing...' },
          { id: 'd', content: 'We are pleased to announce the release of...' }
        ]
      }
    };

    setCurrentExercise(mockExercises[type] || mockExercises.compare);
  };

  // Submit exercise response
  const submitResponse = async () => {
    if (!currentExercise) return;

    const response = {
      exercise_id: currentExercise.id || Date.now(),
      type: currentExercise.type,
      selected: selectedOption,
      rating: rating,
      tags: selectedTags,
      rewrite: customRewrite || null
    };

    try {
      await contentTrainingService.submitExerciseResponse(brandId, response);
      setCompletedCount(prev => prev + 1);

      // Update learned preferences
      if (selectedTags.length > 0) {
        const newPreference = selectedTags[0];
        if (!learnedPreferences.includes(newPreference)) {
          setLearnedPreferences(prev => [...prev.slice(-4), newPreference]);
        }
      }

      successToast('Response recorded');
      generateExercise();
    } catch (error) {
      // Just continue for demo
      setCompletedCount(prev => prev + 1);
      successToast('Response recorded');
      generateExercise();
    }
  };

  // Toggle tag selection
  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Load initial exercise
  useEffect(() => {
    if (hasEnoughSamples && !currentExercise) {
      generateExercise();
    }
  }, [hasEnoughSamples]);

  // Render compare exercise
  const renderCompareExercise = () => (
    <div className={Styles.compareExercise}>
      <div className={Styles.exerciseHeader}>
        <span className={Styles.exerciseLabel}>Compare & Select</span>
        <span className={Styles.exerciseTopic}>Topic: {currentExercise.topic}</span>
      </div>

      <p className={Styles.exercisePrompt}>Which version sounds more like your brand?</p>

      <div className={Styles.optionsGrid}>
        {currentExercise.options?.map((option, idx) => (
          <button
            key={option.id}
            className={`${Styles.optionCard} ${selectedOption === option.id ? Styles.selected : ''}`}
            onClick={() => setSelectedOption(option.id)}
          >
            <span className={Styles.optionLabel}>Version {String.fromCharCode(65 + idx)}</span>
            <p className={Styles.optionContent}>{option.content}</p>
            {selectedOption === option.id && (
              <div className={Styles.selectedBadge}>
                <FiCheck />
              </div>
            )}
          </button>
        ))}
      </div>

      <button
        className={Styles.neitherBtn}
        onClick={() => setSelectedOption('neither')}
      >
        <FiX />
        Neither works for my brand
      </button>
    </div>
  );

  // Render rate exercise
  const renderRateExercise = () => (
    <div className={Styles.rateExercise}>
      <div className={Styles.exerciseHeader}>
        <span className={Styles.exerciseLabel}>Rate Generated Content</span>
        <span className={Styles.exerciseTopic}>Topic: {currentExercise.topic}</span>
      </div>

      <p className={Styles.exercisePrompt}>How well does this match your brand voice?</p>

      <div className={Styles.generatedContent}>
        <div className={Styles.generatedBadge}>AI Generated</div>
        <p>{currentExercise.content}</p>
      </div>

      <div className={Styles.ratingOptions}>
        {RATING_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            className={`${Styles.ratingBtn} ${rating === opt.value ? Styles.selected : ''}`}
            onClick={() => {
              setRating(opt.value);
              setSelectedOption(opt.value);
            }}
          >
            <span className={Styles.ratingEmoji}>{opt.emoji}</span>
            <span className={Styles.ratingLabel}>{opt.label}</span>
          </button>
        ))}
      </div>

      {rating && rating <= 3 && (
        <button
          className={Styles.rewriteToggle}
          onClick={() => setShowRewrite(!showRewrite)}
        >
          <FiEdit3 />
          Rewrite it how you would say it
          <FiChevronRight className={showRewrite ? Styles.rotated : ''} />
        </button>
      )}

      {showRewrite && (
        <textarea
          className={Styles.rewriteInput}
          placeholder="How would you write this?"
          value={customRewrite}
          onChange={(e) => setCustomRewrite(e.target.value)}
          rows={3}
        />
      )}
    </div>
  );

  // Render identify exercise
  const renderIdentifyExercise = () => (
    <div className={Styles.identifyExercise}>
      <div className={Styles.exerciseHeader}>
        <span className={Styles.exerciseLabel}>Spot Your Voice</span>
        <span className={Styles.exerciseTopic}>Topic: {currentExercise.topic}</span>
      </div>

      <p className={Styles.exercisePrompt}>Which of these sounds most like you?</p>

      <div className={Styles.identifyGrid}>
        {currentExercise.options?.map((option, idx) => (
          <button
            key={option.id}
            className={`${Styles.identifyCard} ${selectedOption === option.id ? Styles.selected : ''}`}
            onClick={() => setSelectedOption(option.id)}
          >
            <span className={Styles.identifyLabel}>{String.fromCharCode(65 + idx)}</span>
            <p>{option.content}</p>
            {selectedOption === option.id && (
              <div className={Styles.selectedCheck}>
                <FiCheck />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  // Render exercise based on type
  const renderExercise = () => {
    if (!currentExercise) return null;

    switch (currentExercise.type) {
      case 'compare':
        return renderCompareExercise();
      case 'rate':
        return renderRateExercise();
      case 'identify':
        return renderIdentifyExercise();
      default:
        return renderCompareExercise();
    }
  };

  return (
    <div className={Styles.container}>
      {/* Header */}
      <div className={Styles.header}>
        <div className={Styles.headerText}>
          <h3>Refine Your Voice</h3>
          <p>Help the AI understand your preferences through interactive exercises</p>
        </div>

        {completedCount > 0 && (
          <div className={Styles.progressBadge}>
            <FiAward />
            <span>{completedCount} completed</span>
          </div>
        )}
      </div>

      {/* Exercise Type Selector */}
      <div className={Styles.typeSelector}>
        {EXERCISE_TYPES.map((type) => (
          <button
            key={type.id}
            className={`${Styles.typeBtn} ${exerciseType === type.id ? Styles.active : ''}`}
            onClick={() => {
              setExerciseType(type.id);
              generateExercise(type.id);
            }}
          >
            <span className={Styles.typeLabel}>{type.label}</span>
            <span className={Styles.typeDesc}>{type.description}</span>
          </button>
        ))}
      </div>

      {/* Main Content Area */}
      {!hasEnoughSamples ? (
        <div className={Styles.notEnoughContent}>
          <div className={Styles.emptyIcon}>🎯</div>
          <h4>Add more content first</h4>
          <p>You need at least 3 content samples to start refining your voice. Currently you have {samples.length}.</p>
        </div>
      ) : isLoading ? (
        <div className={Styles.loadingState}>
          <FiRefreshCw className={Styles.spinning} />
          <p>Generating exercise...</p>
        </div>
      ) : (
        <div className={Styles.exerciseContainer}>
          {renderExercise()}

          {/* Feedback Tags */}
          {selectedOption && (
            <div className={Styles.feedbackSection}>
              <p className={Styles.feedbackLabel}>What makes this better? (optional)</p>
              <div className={Styles.feedbackTags}>
                {FEEDBACK_TAGS.map((tag) => (
                  <button
                    key={tag}
                    className={`${Styles.feedbackTag} ${selectedTags.includes(tag) ? Styles.selected : ''}`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className={Styles.actionBar}>
            <button
              className={Styles.skipBtn}
              onClick={() => generateExercise()}
            >
              <FiSkipForward />
              Skip
            </button>

            <button
              className={Styles.submitBtn}
              onClick={submitResponse}
              disabled={!selectedOption}
            >
              Submit & Next
              <FiChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* AI Learning Panel */}
      {learnedPreferences.length > 0 && (
        <div className={Styles.learningPanel}>
          <div className={Styles.learningHeader}>
            <span className={Styles.learningIcon}>🧠</span>
            <span>AI is learning from your choices</span>
          </div>
          <div className={Styles.learnedItems}>
            {learnedPreferences.map((pref, idx) => (
              <span key={idx} className={Styles.learnedItem}>
                <FiCheck /> {pref}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RefineVoice;
